import React from 'react';
import { getFontOptions } from '../config/fontOptions.js';

export const ColumnTypographyControls = ({
    values = {},
    onChange, // (field, value) => void
    customFonts = [],
    showHeading = true,
    showText = true,
    headingTitle = '📝 Column Heading Typography',
    textTitle = '📄 Column Body Text Typography'
}) => {
    const fonts = getFontOptions(customFonts, true, '', 'Default / Inherit');

    const handlePropChange = (field, val) => {
        if (!onChange) return;
        onChange(field, val);
        // Bidirectional normalization
        if (field === 'headingWeight') onChange('headingFontWeight', val);
        if (field === 'headingFontWeight') onChange('headingWeight', val);
        if (field === 'headingSize') onChange('headingFontSize', val);
        if (field === 'headingFontSize') onChange('headingSize', val);
        if (field === 'headingTransform') onChange('headingTextTransform', val);
        if (field === 'headingTextTransform') onChange('headingTransform', val);

        if (field === 'textWeight') onChange('textFontWeight', val);
        if (field === 'textFontWeight') onChange('textWeight', val);
        if (field === 'textSize') onChange('textFontSize', val);
        if (field === 'textFontSize') onChange('textSize', val);
        if (field === 'textTransform') onChange('textTextTransform', val);
        if (field === 'textTextTransform') onChange('textTransform', val);
    };

    const settingLabelStyle = {
        fontSize: '7.5px',
        color: '#64748b',
        display: 'block',
        marginBottom: '2px',
        fontWeight: '600'
    };

    const inputStyle = {
        padding: '3px 5px',
        border: '1px solid #cbd5e1',
        borderRadius: '3px',
        fontSize: '9px',
        width: '100%',
        background: '#fff',
        color: '#334155',
        boxSizing: 'border-box',
        outline: 'none'
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* HEADING TYPOGRAPHY */}
            {showHeading && (
                <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{headingTitle}</span>
                    </div>

                    {/* Font Family */}
                    <div>
                        <label style={settingLabelStyle}>Font Family</label>
                        <select
                            value={values.headingFontFamily || ''}
                            onChange={e => handlePropChange('headingFontFamily', e.target.value)}
                            style={inputStyle}
                        >
                            {fonts.map((f, idx) => (
                                <option key={idx} value={f.value}>{f.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Font Size & Font Weight */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                        <div>
                            <label style={settingLabelStyle}>Font Size</label>
                            <input
                                type="text"
                                value={values.headingSize || values.headingFontSize || ''}
                                placeholder="e.g. 1.25rem or 20px"
                                onChange={e => handlePropChange('headingSize', e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={settingLabelStyle}>Font Weight</label>
                            <select
                                value={values.headingWeight || values.headingFontWeight || ''}
                                onChange={e => handlePropChange('headingWeight', e.target.value)}
                                style={inputStyle}
                            >
                                <option value="">Default (Inherit)</option>
                                <option value="100">100 - Thin</option>
                                <option value="200">200 - Extra Light</option>
                                <option value="300">300 - Light</option>
                                <option value="400">400 - Normal</option>
                                <option value="500">500 - Medium</option>
                                <option value="600">600 - Semi-Bold</option>
                                <option value="700">700 - Bold</option>
                                <option value="800">800 - Extra-Bold</option>
                                <option value="900">900 - Black</option>
                            </select>
                        </div>
                    </div>

                    {/* Line Height & Letter Spacing */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                        <div>
                            <label style={settingLabelStyle}>Line Height</label>
                            <input
                                type="text"
                                value={values.headingLineHeight || ''}
                                placeholder="e.g. 1.2, 1.4, 28px"
                                onChange={e => handlePropChange('headingLineHeight', e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={settingLabelStyle}>Letter Spacing</label>
                            <input
                                type="text"
                                value={values.headingLetterSpacing || ''}
                                placeholder="e.g. 0.05em or 1px"
                                onChange={e => handlePropChange('headingLetterSpacing', e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                    </div>

                    {/* Alignment & Text Transform */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                        <div>
                            <label style={settingLabelStyle}>Alignment</label>
                            <select
                                value={values.headingAlign || ''}
                                onChange={e => handlePropChange('headingAlign', e.target.value)}
                                style={inputStyle}
                            >
                                <option value="">Default (Left)</option>
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                                <option value="justify">Justify</option>
                            </select>
                        </div>
                        <div>
                            <label style={settingLabelStyle}>Text Transform</label>
                            <select
                                value={values.headingTransform || values.headingTextTransform || 'none'}
                                onChange={e => handlePropChange('headingTransform', e.target.value)}
                                style={inputStyle}
                            >
                                <option value="none">Normal</option>
                                <option value="uppercase">UPPERCASE</option>
                                <option value="lowercase">lowercase</option>
                                <option value="capitalize">Capitalize</option>
                            </select>
                        </div>
                    </div>

                    {/* Heading Color */}
                    <div>
                        <label style={settingLabelStyle}>Heading Color</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input
                                type="color"
                                value={(values.headingColor && values.headingColor.startsWith('#')) ? values.headingColor : '#1e293b'}
                                onChange={e => handlePropChange('headingColor', e.target.value)}
                                style={{ width: '20px', height: '20px', padding: 0, border: 'none', borderRadius: '3px', cursor: 'pointer', flexShrink: 0 }}
                            />
                            <input
                                type="text"
                                value={values.headingColor || ''}
                                placeholder="Inherit / #1e293b"
                                onChange={e => handlePropChange('headingColor', e.target.value)}
                                style={{ ...inputStyle, flex: 1 }}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* BODY TEXT TYPOGRAPHY */}
            {showText && (
                <div style={{ background: '#f8fafc', padding: '6px 8px', borderRadius: '4px', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 'bold', color: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>{textTitle}</span>
                    </div>

                    {/* Font Family */}
                    <div>
                        <label style={settingLabelStyle}>Font Family</label>
                        <select
                            value={values.textFontFamily || ''}
                            onChange={e => handlePropChange('textFontFamily', e.target.value)}
                            style={inputStyle}
                        >
                            {fonts.map((f, idx) => (
                                <option key={idx} value={f.value}>{f.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Font Size & Font Weight */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                        <div>
                            <label style={settingLabelStyle}>Font Size</label>
                            <input
                                type="text"
                                value={values.textSize || values.textFontSize || ''}
                                placeholder="e.g. 1rem or 16px"
                                onChange={e => handlePropChange('textSize', e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={settingLabelStyle}>Font Weight</label>
                            <select
                                value={values.textWeight || values.textFontWeight || ''}
                                onChange={e => handlePropChange('textWeight', e.target.value)}
                                style={inputStyle}
                            >
                                <option value="">Default (Inherit)</option>
                                <option value="100">100 - Thin</option>
                                <option value="200">200 - Extra Light</option>
                                <option value="300">300 - Light</option>
                                <option value="400">400 - Normal</option>
                                <option value="500">500 - Medium</option>
                                <option value="600">600 - Semi-Bold</option>
                                <option value="700">700 - Bold</option>
                                <option value="800">800 - Extra-Bold</option>
                                <option value="900">900 - Black</option>
                            </select>
                        </div>
                    </div>

                    {/* Line Height & Letter Spacing */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                        <div>
                            <label style={settingLabelStyle}>Line Height</label>
                            <input
                                type="text"
                                value={values.textLineHeight || ''}
                                placeholder="e.g. 1.5, 1.6, 24px"
                                onChange={e => handlePropChange('textLineHeight', e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                        <div>
                            <label style={settingLabelStyle}>Letter Spacing</label>
                            <input
                                type="text"
                                value={values.textLetterSpacing || ''}
                                placeholder="e.g. normal or 0.5px"
                                onChange={e => handlePropChange('textLetterSpacing', e.target.value)}
                                style={inputStyle}
                            />
                        </div>
                    </div>

                    {/* Alignment & Text Transform */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px' }}>
                        <div>
                            <label style={settingLabelStyle}>Alignment</label>
                            <select
                                value={values.textAlign || ''}
                                onChange={e => handlePropChange('textAlign', e.target.value)}
                                style={inputStyle}
                            >
                                <option value="">Default (Left)</option>
                                <option value="left">Left</option>
                                <option value="center">Center</option>
                                <option value="right">Right</option>
                                <option value="justify">Justify</option>
                            </select>
                        </div>
                        <div>
                            <label style={settingLabelStyle}>Text Transform</label>
                            <select
                                value={values.textTransform || values.textTextTransform || 'none'}
                                onChange={e => handlePropChange('textTransform', e.target.value)}
                                style={inputStyle}
                            >
                                <option value="none">Normal</option>
                                <option value="uppercase">UPPERCASE</option>
                                <option value="lowercase">lowercase</option>
                                <option value="capitalize">Capitalize</option>
                            </select>
                        </div>
                    </div>

                    {/* Body Text Color */}
                    <div>
                        <label style={settingLabelStyle}>Body Text Color</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input
                                type="color"
                                value={(values.textColor && values.textColor.startsWith('#')) ? values.textColor : '#475569'}
                                onChange={e => handlePropChange('textColor', e.target.value)}
                                style={{ width: '20px', height: '20px', padding: 0, border: 'none', borderRadius: '3px', cursor: 'pointer', flexShrink: 0 }}
                            />
                            <input
                                type="text"
                                value={values.textColor || ''}
                                placeholder="Inherit / #475569"
                                onChange={e => handlePropChange('textColor', e.target.value)}
                                style={{ ...inputStyle, flex: 1 }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ColumnTypographyControls;

